import { Link } from "react-router-dom";
import {
  PARTNER_NORMALIZATION_ISSUE_LABELS,
  VEHICLE_IDENTITY_HEALTH_SCORE_LABELS,
  VEHICLE_IDENTITY_ISSUE_SEVERITY_LABELS,
  vehicleIdentityIssueLabel,
  formatCoveragePercent,
  vehicleIdentityHealthScoreClass,
} from "../../../constants/partnerVehicleIdentityHealthConstants";

function CoverageCard({ label, value, helper }) {
  return (
    <div className="partner-vehicle-identity-health__coverage-card">
      <p className="partner-vehicle-identity-health__coverage-value">{formatCoveragePercent(value)}</p>
      <p className="partner-vehicle-identity-health__coverage-label">{label}</p>
      {helper ? <p className="muted partner-vehicle-identity-health__coverage-helper">{helper}</p> : null}
    </div>
  );
}

export default function PartnerVehicleIdentityHealthSection({ vehicleIdentity, path, dealerId = "" }) {
  if (!vehicleIdentity) return null;

  const {
    label,
    score,
    message,
    totalListings = 0,
    coverage = {},
    needsReviewCount = 0,
    issues = {},
    topIssues = [],
    recommendations = [],
    duplicateGroupCount = 0,
  } = vehicleIdentity;

  const hasIssues =
    needsReviewCount > 0 ||
    duplicateGroupCount > 0 ||
    Object.values(issues).some((count) => count > 0);

  const issueEntries = Object.entries(issues).filter(([, count]) => count > 0);

  return (
    <section className="partner-panel partner-vehicle-identity-health" aria-labelledby="vehicle-identity-health-title">
      <div className="partner-panel__header">
        <div>
          <h3 id="vehicle-identity-health-title">Vehicle name health</h3>
          <p className="muted partner-health-section-intro">
            Monitor canonical Make, Model, and Trim coverage across dealer inventory. Trim is optional and
            does not block listing visibility.
          </p>
        </div>
        <div
          className={`partner-vehicle-identity-health__score ${vehicleIdentityHealthScoreClass(score)}`}
          aria-label={`Vehicle name health score: ${label}`}
        >
          <span className="partner-vehicle-identity-health__score-label">
            {VEHICLE_IDENTITY_HEALTH_SCORE_LABELS[score] || label}
          </span>
        </div>
      </div>

      <p className="partner-vehicle-identity-health__message">{message}</p>

      <div className="partner-vehicle-identity-health__hero-stats">
        <div className="partner-leads-stat partner-leads-stat--blue">
          <span>Make + Model</span>
          <strong>{formatCoveragePercent(coverage.makeModelCoverage)}</strong>
        </div>
        <div className="partner-leads-stat partner-leads-stat--amber">
          <span>Needs review</span>
          <strong>{needsReviewCount.toLocaleString()}</strong>
        </div>
        <div className="partner-leads-stat">
          <span>Listings</span>
          <strong>{totalListings.toLocaleString()}</strong>
        </div>
      </div>

      <div className="partner-vehicle-identity-health__coverage-grid">
        <CoverageCard label="Make coverage" value={coverage.makeCoverage} />
        <CoverageCard label="Model coverage" value={coverage.modelCoverage} />
        <CoverageCard
          label="Trim coverage"
          value={coverage.trimCoverage}
          helper="Optional — improves grouped inventory"
        />
        <CoverageCard label="Needs review" value={totalListings ? needsReviewCount / totalListings : 0} />
      </div>

      {!hasIssues ? (
        <div className="partner-vehicle-identity-health__empty">
          <p className="muted">
            Vehicle names look healthy. New unmatched or low-confidence listings will appear here when they
            need attention.
          </p>
        </div>
      ) : (
        <>
          {issueEntries.length ? (
            <div className="partner-vehicle-identity-health__issues">
              <h4 className="partner-inventory-health__subheading">Issue breakdown</h4>
              <div className="partner-vehicle-identity-health__issue-grid">
                {issueEntries.map(([issueType, count]) => (
                  <div key={issueType} className="partner-vehicle-identity-health__issue-chip">
                    <strong>{vehicleIdentityIssueLabel(issueType)}</strong>
                    <span>{count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {topIssues.length ? (
            <div className="partner-vehicle-identity-health__top-issues">
              <h4 className="partner-inventory-health__subheading">Top problems</h4>
              <div className="partner-health-table-wrap">
                <table className="partner-health-table">
                  <thead>
                    <tr>
                      <th>Issue</th>
                      <th>Count</th>
                      <th>Severity</th>
                      <th>Example</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {topIssues.map((issue) => (
                      <tr key={`${issue.issueType}-${issue.exampleRawValue || issue.count}`}>
                        <td>{vehicleIdentityIssueLabel(issue.issueType)}</td>
                        <td>{issue.count}</td>
                        <td>{VEHICLE_IDENTITY_ISSUE_SEVERITY_LABELS[issue.severity] || issue.severity}</td>
                        <td>{issue.exampleRawValue || "—"}</td>
                        <td>
                          <Link className="partner-health-action" to={path(issue.actionUrl)}>
                            Review
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      )}

      {recommendations.length ? (
        <div className="partner-inventory-health__queue-links partner-vehicle-identity-health__actions">
          {recommendations.map((entry) => (
            <Link key={entry.url} className="partner-dashboard-btn" to={path(entry.url)}>
              {entry.label}
            </Link>
          ))}
          {dealerId ? (
            <Link className="partner-dashboard-btn" to={path(`dealers/${dealerId}/inventory/normalization`)}>
              Fix vehicle names
            </Link>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
